import { makeOverridableFactory } from '../../common/utils/make-overridable-factory';
import { waitForElement } from '../../common/utils/wait-for-element';
import { getPortalId } from '../../common/utils/get-portal-id';
const isInstanceOfCallbackResolvable = resolvable => typeof resolvable === 'function';
const resolve = (resolvable, params) => isInstanceOfCallbackResolvable(resolvable) ? resolvable(params) : resolvable;

// -- CDefBuilder conditional member interfaces --

// -- CDefBuilder always-available member interfaces --

// -- CDefBuilder: determines build readiness --

// -- DefinitionConstruct: internal builder state --

function assertConstructValid(construct, requiredFields) {
  for (const field of requiredFields) {
    if (construct[field] === null) {
      throw new Error(`ComponentDefinitionBuilder: "${field}" is required for "${construct.name}".`);
    }
  }
  if (construct.propsSchema !== undefined && construct.initialProps === undefined) {
    throw new Error(`ComponentDefinitionBuilder: "initialProps" is required when "propsSchema" is set for "${construct.name}". Call setInitialProps().`);
  }
}
const resolveRenderTarget = (target, params) => {
  if (target === undefined) {
    return undefined;
  }
  if (typeof target === 'string') {
    return () => waitForElement(target);
  }
  if (typeof target === 'function') {
    return () => target(params);
  }
  return () => target;
};
const resolveQueryParams = (qp, params) => {
  if (qp === undefined) {
    return undefined;
  }
  if (typeof qp !== 'function') {
    return qp;
  }
  const fn = qp;
  return () => Promise.resolve(fn(params));
};
const replacePortalId = value => {
  const portalId = getPortalId();
  if (portalId === undefined) {
    return value;
  }
  return value.replace(':portalId', String(portalId));
};

// -- ComponentDefinitionBuilder class --

export class ComponentDefinitionBuilder {
  constructor(construct) {
    this.construct = construct;
  }
  static builder(name) {
    return new ComponentDefinitionBuilder({
      name,
      source: null,
      definitionParamsSchema: undefined,
      propsSchema: undefined,
      initialProps: undefined,
      functionsSchema: undefined,
      capabilities: null,
      capabilityDefs: [],
      queryParams: undefined,
      iframeProps: undefined,
      renderTarget: undefined,
      scrolling: undefined,
      tags: undefined,
      enableHubless: undefined,
      ref: undefined,
      loadInstantly: undefined,
      initiallyOpen: undefined,
      initiallyFullscreen: undefined,
      platformName: null
    });
  }
  setPath(path) {
    this.construct.source = {
      type: 'path',
      path
    };
    return this;
  }

  /** @deprecated Use setPath instead */
  setSrc(src) {
    this.construct.source = {
      type: 'src',
      src
    };
    return this;
  }
  setCapabilities(capabilities) {
    this.applyCapabilities(capabilities, []);
    return this;
  }
  setCapabilityDefs(capabilityDefs) {
    const names = capabilityDefs.map(c => c.getName());
    this.applyCapabilities(names, capabilityDefs);
    return this;
  }
  applyCapabilities(capabilities, capabilityDefs) {
    this.construct.capabilities = capabilities;
    this.construct.capabilityDefs = capabilityDefs;
  }
  setPlatformName(name) {
    this.construct.platformName = name;
    return this;
  }
  setQueryParams(qp) {
    this.construct.queryParams = qp;
    return this;
  }
  setDefinitionParams(schema) {
    this.construct.definitionParamsSchema = schema;
    return this;
  }
  setPropsSchema(schema) {
    this.construct.propsSchema = schema;
    this.construct.initialProps = undefined;
    return this;
  }
  setInitialProps(props) {
    this.construct.initialProps = props;
    return this;
  }
  setFunctionsSchema(functions) {
    this.construct.functionsSchema = functions;
    return this;
  }
  setIframeProps(iframeProps) {
    this.construct.iframeProps = iframeProps;
    return this;
  }
  setRenderTarget(target) {
    this.construct.renderTarget = target;
    return this;
  }
  setScrolling(scrolling) {
    this.construct.scrolling = scrolling;
    return this;
  }
  setTags(tags) {
    this.construct.tags = tags;
    return this;
  }
  setEnableHubless(enable) {
    this.construct.enableHubless = enable;
    return this;
  }
  setRef(ref) {
    this.construct.ref = ref;
    return this;
  }
  dangerouslySetLoadInstantly(next) {
    this.construct.loadInstantly = next;
    return this;
  }
  setInitiallyOpen(open) {
    this.construct.initiallyOpen = open;
    return this;
  }
  setInitiallyFullscreen(fullscreen) {
    this.construct.initiallyFullscreen = fullscreen;
    return this;
  }
  build() {
    const construct = Object.assign({}, this.construct);
    assertConstructValid(construct, ComponentDefinitionBuilder.REQUIRED_FIELDS);
    const {
      name,
      source,
      capabilities,
      capabilityDefs,
      propsSchema,
      initialProps,
      functionsSchema,
      queryParams,
      iframeProps,
      renderTarget,
      scrolling,
      tags,
      enableHubless,
      ref,
      loadInstantly,
      initiallyOpen,
      initiallyFullscreen,
      platformName
    } = construct;
    const factory = params => {
      const def = {
        name,
        capabilities,
        capabilityDefs
      };
      if (source.type === 'path') {
        def.path = replacePortalId(source.path);
        const resolvedQp = resolveQueryParams(queryParams, params);
        if (resolvedQp !== undefined) {
          def.queryParams = resolvedQp;
        }
      } else {
        def.src = replacePortalId(source.src);
      }
      if (propsSchema !== undefined) {
        def.propsSchema = propsSchema;
        def.initialProps = resolve(initialProps, params);
      }
      if (functionsSchema !== undefined) {
        def.functions = functionsSchema;
      }
      if (iframeProps !== undefined) {
        def.iframeProps = resolve(iframeProps, params);
      }
      const renderTo = resolveRenderTarget(renderTarget, params);
      if (renderTo !== undefined) {
        def.renderTo = renderTo;
      }
      if (scrolling !== undefined) {
        def.scrolling = scrolling;
      }
      if (tags !== undefined) {
        def.tags = tags;
      }
      if (enableHubless !== undefined) {
        def.enableHubless = enableHubless;
      }
      if (ref !== undefined) {
        def.ref = resolve(ref, params);
      }
      if (loadInstantly !== undefined) {
        def.deferLoading = !resolve(loadInstantly, params);
      }
      if (initiallyOpen !== undefined) {
        def.initiallyOpen = resolve(initiallyOpen, params);
      }
      if (initiallyFullscreen !== undefined) {
        def.initiallyFullscreen = initiallyFullscreen;
      }
      if (platformName !== undefined) {
        def.platformName = platformName;
      }
      return def;
    };
    const overridableDefinition = makeOverridableFactory(factory);
    const getName = () => name;
    const getPlatformName = () => platformName;
    return Object.assign(overridableDefinition, {
      getName,
      getPlatformName
    });
  }
}
ComponentDefinitionBuilder.REQUIRED_FIELDS = ['source', 'capabilities', 'platformName'];